import { useState, useEffect, useCallback } from 'react'
import { Capacitor } from '@capacitor/core'
import { onUpgradeRequired } from '../shared/api/base'

interface VersionState {
  /** A new web version is available (detected via version.json polling) */
  updateAvailable: boolean
  /** Server requires a newer version (426 response received) */
  upgradeRequired: boolean
  /** The minimum version the server requires (from 426 response) */
  requiredVersion: string | null
}

const POLL_INTERVAL = 60_000 // 60 seconds

/**
 * Detects when a newer app version is available.
 *
 * Two detection methods:
 * 1. Polls /version.json every 60s (web only) — sets `updateAvailable`
 * 2. Listens for 426 Upgrade Required from API — sets `upgradeRequired`
 *
 * For native (Capacitor) apps, only the 426 detection works since
 * version.json is bundled inside the native binary.
 */
export function useVersionCheck() {
  const [state, setState] = useState<VersionState>({
    updateAvailable: false,
    upgradeRequired: false,
    requiredVersion: null,
  })

  // Listen for 426 Upgrade Required responses
  useEffect(() => {
    return onUpgradeRequired((minVersion) => {
      setState(prev => ({ ...prev, upgradeRequired: true, requiredVersion: minVersion }))
    })
  }, [])

  // Poll version.json for web clients (not native — the JSON is bundled in the binary)
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return

    let cancelled = false

    const check = async () => {
      try {
        const res = await fetch('/version.json', { cache: 'no-cache' })
        if (!res.ok) return
        const data = await res.json()
        if (data.version && data.version !== __APP_VERSION__ && !cancelled) {
          setState(prev => ({ ...prev, updateAvailable: true }))
        }
      } catch {
        // Network error — ignore, will retry next interval
      }
    }

    // First check after a short delay (don't block startup)
    const initialTimeout = setTimeout(check, 5_000)
    const interval = setInterval(check, POLL_INTERVAL)

    return () => {
      cancelled = true
      clearTimeout(initialTimeout)
      clearInterval(interval)
    }
  }, [])

  const refresh = useCallback(() => {
    window.location.reload()
  }, [])

  return { ...state, refresh }
}
